import * as BABYLON from 'babylonjs';
import NodeEngine from 'shared/libraries/mwc/3d/nodeGraph/nodeEngine';
import UIGlobals from '../uiGlobals';
import CoreUtils from '../../utils/coreUtils';
import Utils2D from '../../utils/utils2D';
import MemoryCacher, { CACHE_IDS } from './memoryCacher.js';
import GlobalDefs from '../../globalDefs.js';

const Vector3 = BABYLON.Vector3;
const Dot3 = BABYLON.Vector3.Dot;
let memCacher = new MemoryCacher();

let _cache_rotPoint_Quat = null;
let _cache_rotPoint_Mat = null;

let _cache_tricd_tMin = BABYLON.Vector3.Zero();
let _cache_tricd_tMax = BABYLON.Vector3.Zero();
let _cache_tricd_absNormal = BABYLON.Vector3.Zero();
let _cache_tricd_faceNormal = BABYLON.Vector3.Zero();
const _cache_tricd_t = [BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero()];
const _cache_tricd_absT = [BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero(), BABYLON.Vector3.Zero()];

let _cache_snapping_directions = [    
    new BABYLON.Vector3(1, 0, 0),
    new BABYLON.Vector3(0, 0, 1),
    new BABYLON.Vector3(-1, 0, 0),
    new BABYLON.Vector3(0, 0, -1),
    new BABYLON.Vector3(Math.SQRT1_2, 0, Math.SQRT1_2),
    new BABYLON.Vector3(-Math.SQRT1_2, 0, Math.SQRT1_2),
    new BABYLON.Vector3(-Math.SQRT1_2, 0, -Math.SQRT1_2),
    new BABYLON.Vector3(Math.SQRT1_2, 0, -Math.SQRT1_2),];
let _cache_snapping_increment = 45;

export default class Utils3D
{
/*****************************************************************************/
static getBabylonVersion()
{
    let strVer = BABYLON.Engine.Version;
    return parseFloat(strVer);
}
/*****************************************************************************/
// Please don't use this since it doesn't handle multi-floor scenarios. Consider
// using RoomView.getPointOnActiveFloorLevel() instead.
static getPointOnGround(ptX, ptY, camera)
{
	return Utils3D.getPointOnPlane(ptX, ptY, new BABYLON.Vector3(0, 1, 0), 0, camera);
}
/*****************************************************************************/
static getPointOnPlane(ptX, ptY, vecNormal, fDistToOrigin, camera, fOptNearClippingMult)
{
	let pRes = Utils3D.getPointOnPlaneHelper(ptX, ptY, vecNormal, fDistToOrigin, camera, fOptNearClippingMult);
	if(!pRes)
		pRes = Utils3D.getPointOnPlaneHelper(ptX, ptY, vecNormal.scale(-1.0), -fDistToOrigin, camera, fOptNearClippingMult);
	return pRes;
}
/*****************************************************************************/
static getPointOnPlaneHelper(ptX, ptY, vecNormal, fDistToOrigin, camera, fOptNearClippingMult)
{
	let scene = camera.getScene();

	// Note that this is created accounting for the minZ near-clipping plane,
	// and will not intersect objects closer than that.
	let pRay = scene.createPickingRay(ptX, ptY, null, camera);

	// Babylon creates an offset ray origin - where the clipping starts. But there's a bug
	// where the actual world position of the clipping plane is actually half
	// the actual camera minZ value.
	// But for ortho cams, this breaks because their origin is always 0 in x and z.
	if(camera.camClass !== UIGlobals.CameraClassType.TOP_DOWN && camera.mode !== BABYLON.Camera.ORTHOGRAPHIC_CAMERA)
	{
		if(fOptNearClippingMult)
			pRay.origin = camera.position.add(pRay.direction.scale(camera.minZ*fOptNearClippingMult));
		else
			pRay.origin = camera.position.add(pRay.direction.scale(camera.minZ));
	}

	let result1 = BABYLON.Vector3.Dot(vecNormal, pRay.direction);
	if(Math.abs(result1) < 9.99999997475243E-07)
		return null;

	let result2 = BABYLON.Vector3.Dot(vecNormal, pRay.origin);
	let distance = (fDistToOrigin - result2) / result1;
	if(distance < 0.0)
	{
		if(distance < -9.99999997475243E-07)
			return null;
		else
			distance = 0;
	}

	return pRay.origin.add(pRay.direction.scale(distance));
}
/*****************************************************************************/
static getGroundPosition(ptX, ptY, camera, fOptHeight)
{
	let scene = camera.getScene();

	let fScale = 1.0;
	if(scene.hardwareScalingLevel)
		fScale = scene.hardwareScalingLevel;

	let pRay = scene.createPickingRay(ptX*fScale, ptY*fScale, null, camera);

	// Babylon creates an offset ray origin - where
	// the clipping starts. Here, we optionally reset the origin closer to
	// the actual camera location.
	// But for ortho cams, this breaks because their origin is always 0 in x and z.
	if(camera.camClass !== UIGlobals.CameraClassType.TOP_DOWN && camera.mode !== BABYLON.Camera.ORTHOGRAPHIC_CAMERA)
		pRay.origin = camera.position.add(pRay.direction.scale(camera.minZ));

    let pPlaneNormal = new BABYLON.Vector3(0, 1, 0);
    let result1 = BABYLON.Vector3.Dot(pPlaneNormal, pRay.direction);
    if(Math.abs(result1) < 9.99999997475243E-07)
        return null;

    if(fOptHeight === undefined || fOptHeight === null)
    	fOptHeight = 0;

    let result2 = BABYLON.Vector3.Dot(pPlaneNormal, pRay.origin);
    // The 0 below is the offset along the normal of the plane.
    let distance = (fOptHeight - result2) / result1;
    if(distance < 0.0)
    {
        if(distance < -9.99999997475243E-07)
            return null;
        else
            distance = 0;
    }

    return pRay.origin.add(pRay.direction.scale(distance));
}
/*****************************************************************************/
static getProjectionParameters(camera)
{
	let pRes = {};

	let scene = camera.getScene();
	scene.updateTransformMatrix(true);

	pRes.pSceneTransformMatrix = scene.getTransformMatrix();
	pRes.pGlobalViewport = Utils3D.getGlobalViewport(camera);
	return pRes;
}
/*****************************************************************************/
static getGlobalViewport(camera)
{
	let scene = camera.getScene();
    return camera.viewport.toGlobal(scene.getEngine().getRenderWidth(), scene.getEngine().getRenderHeight());
}
/*****************************************************************************/
static projectTo2D(vecPoint3D, camera)
{
	let pProjParms = Utils3D.getProjectionParameters(camera);
	let pSceneTransformMatrix = pProjParms.pSceneTransformMatrix;
	let pGlobalViewport = pProjParms.pGlobalViewport;

	let vecRes = BABYLON.Vector3.Project(vecPoint3D, BABYLON.Matrix.Identity(), pSceneTransformMatrix, pGlobalViewport);
	return new BABYLON.Vector2(vecRes.x, vecRes.y);
}
/*****************************************************************************/
static getScreenSpaceDistanceBetweenPoints(origPoint3D, otherPoint3D, camera)
{
    const origPoint2D = Utils3D.projectTo2D(origPoint3D, camera);
    const otherPoint2D = Utils3D.projectTo2D(otherPoint3D, camera);
    return otherPoint2D.subtract(origPoint2D).length();
}
/*****************************************************************************/
static screenDistanceToWorld(iPixelDistance, vecPoint, camera, bUseMinDistance = false)
{
	// Get the scale on three axes, pick the largest:
	let distances = [];

	let tempPoint = BABYLON.Vector3.Zero();
	tempPoint.copyFromFloats(vecPoint.x + 1.0, vecPoint.y, vecPoint.z);
	distances[0] = Utils3D.getScreenSpaceDistanceBetweenPoints(vecPoint, tempPoint, camera);

	tempPoint.copyFromFloats(vecPoint.x, vecPoint.y + 1.0, vecPoint.z);
	distances[1] = Utils3D.getScreenSpaceDistanceBetweenPoints(vecPoint, tempPoint, camera);

	tempPoint.copyFromFloats(vecPoint.x, vecPoint.y, vecPoint.z + 1.0);
	distances[2] = Utils3D.getScreenSpaceDistanceBetweenPoints(vecPoint, tempPoint, camera);

	if(bUseMinDistance)
		return iPixelDistance/Math.min(...distances);
	else
		return iPixelDistance/Math.max(...distances);
}
/*****************************************************************************/
static getDistanceToCamera(point3D, camera)
{
	let cameraPosition = camera.position;
	return point3D.subtract(cameraPosition).length();
}
/*****************************************************************************/
static includePoint(vecPoint, vecMin, vecMax)
{
	if(vecPoint.x < vecMin.x)
		vecMin.x = vecPoint.x;
	if(vecPoint.y < vecMin.y)
		vecMin.y = vecPoint.y;
	if(vecPoint.z < vecMin.z)
		vecMin.z = vecPoint.z;

	if(vecPoint.x > vecMax.x)
		vecMax.x = vecPoint.x;
	if(vecPoint.y > vecMax.y)
		vecMax.y = vecPoint.y;
	if(vecPoint.z > vecMax.z)
		vecMax.z = vecPoint.z;
}
/*****************************************************************************/
static includeBBox(pBBoxInOut, pOtherBBox)
{
	if(pOtherBBox.minimumWorld.x < pBBoxInOut.minimumWorld.x)
		pBBoxInOut.minimumWorld.x = pOtherBBox.minimumWorld.x;
	if(pOtherBBox.minimumWorld.y < pBBoxInOut.minimumWorld.y)
		pBBoxInOut.minimumWorld.y = pOtherBBox.minimumWorld.y;
	if(pOtherBBox.minimumWorld.z < pBBoxInOut.minimumWorld.z)
		pBBoxInOut.minimumWorld.z = pOtherBBox.minimumWorld.z;

	if(pOtherBBox.maximumWorld.x > pBBoxInOut.maximumWorld.x)
		pBBoxInOut.maximumWorld.x = pOtherBBox.maximumWorld.x;
	if(pOtherBBox.maximumWorld.y > pBBoxInOut.maximumWorld.y)
		pBBoxInOut.maximumWorld.y = pOtherBBox.maximumWorld.y;
	if(pOtherBBox.maximumWorld.z > pBBoxInOut.maximumWorld.z)
		pBBoxInOut.maximumWorld.z = pOtherBBox.maximumWorld.z;
}
/*****************************************************************************/
static removeCoincidentPoints(vecPoints, fDistance, isLoop)
{
	if(fDistance === null || fDistance === undefined)
		return;

	let iNext, iCurr, iNum = vecPoints.length;
	if(iNum <= 2)
		return;

	for(iCurr = iNum - 2; iCurr >= 0; iCurr--)
	{
		iNext = iCurr + 1;
		if(vecPoints[iCurr].subtract(vecPoints[iNext]).length() <= fDistance)
		{
			if(iCurr > 0)
				vecPoints.splice(iCurr, 1);
			else if(iNext < vecPoints.length)
				vecPoints.splice(iNext, 1);
		}
	}
	if(isLoop && vecPoints[0].subtract(vecPoints[vecPoints.length - 1]).length() <= fDistance) {
		vecPoints.splice(vecPoints.length - 1,1);
	}
}
/*****************************************************************************/
static getSceneBBox(pScene, bIncludeLightMeshes = false, meshFilter = (mesh) => true)
{
	let vecSize = BABYLON.Vector3.Zero();
	let resBBox = new BABYLON.BoundingBox(new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE), new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE));
	for(let index = 0; index < pScene.meshes.length; index++)
	{
		let m = pScene.meshes[index];
		if(m.isGround || m.visibility < 0.001 || m.isCached || !m.isEnabled())
			continue;

		if(!bIncludeLightMeshes && m.relatedLight)
			continue;

		if(!meshFilter(m))
			continue;

		m.computeWorldMatrix();

		let bbox = m.getBoundingInfo().boundingBox;
		bbox.maximumWorld.subtractToRef(bbox.minimumWorld, vecSize);
		// We also have nodes (such as root node)
		// that have no meshes and result in a
		// zero-dimension box centered at the origin.
		// We ignore this, because otherwise we'd
		// always include the origin in our calculations.
		if(Math.abs(vecSize.x) < 0.0001 && Math.abs(vecSize.y) < 0.0001 && Math.abs(vecSize.z) < 0.0001)
			continue;

		if(bbox.minimumWorld.x < resBBox.minimumWorld.x)
			resBBox.minimumWorld.x = bbox.minimumWorld.x;
		if(bbox.minimumWorld.y < resBBox.minimumWorld.y)
			resBBox.minimumWorld.y = bbox.minimumWorld.y;
		if(bbox.minimumWorld.z < resBBox.minimumWorld.z)
			resBBox.minimumWorld.z = bbox.minimumWorld.z;

		if(bbox.maximumWorld.x > resBBox.maximumWorld.x)
			resBBox.maximumWorld.x = bbox.maximumWorld.x;
		if(bbox.maximumWorld.y > resBBox.maximumWorld.y)
			resBBox.maximumWorld.y = bbox.maximumWorld.y;
		if(bbox.maximumWorld.z > resBBox.maximumWorld.z)
			resBBox.maximumWorld.z = bbox.maximumWorld.z;
	}

	// If we haven't had any objects, set it to 0s.
	if(resBBox.minimumWorld.x === Number.MAX_VALUE)
	{
		resBBox.minimumWorld.x = 0;
		resBBox.minimumWorld.y = 0;
		resBBox.minimumWorld.z = 0;

		resBBox.maximumWorld.x = 0;
		resBBox.maximumWorld.y = 0;
		resBBox.maximumWorld.z = 0;
	}

	return new BABYLON.BoundingBox(resBBox.minimumWorld.clone(), resBBox.maximumWorld.clone());
}
/*****************************************************************************/
static rotateMeshArbitraryVec(mesh, axis, fDegRotation)
{
    let rotationAmount = fDegRotation / 180.0 * Math.PI;
    let quatRotation = BABYLON.Quaternion.RotationAxis(axis, rotationAmount);
    mesh.rotationQuaternion = mesh.pickedObjectInitRotation.multiply(quatRotation);
    mesh.accumOnlyQuaternion = mesh.pickedObjectInitAccumQuaternion.multiply(quatRotation);

    let radRotation = quatRotation.toEulerAngles();

    mesh.lastRotation[0] = radRotation.x + mesh.pickedObjectInitRadRotation[0];
    mesh.lastRotation[1] = radRotation.y + mesh.pickedObjectInitRadRotation[1];
    mesh.lastRotation[2] = radRotation.z + mesh.pickedObjectInitRadRotation[2];

    return fDegRotation;
}
/*****************************************************************************/
// Compute bounding box of a list of points
// Input:
//	- pPoints: BABYLON.Vector3[]
// Output: object with properties 'min' and 'max', containing a BABYLON.Vector3 to
// 		   indicate the bounding box
static computePointsBBox(pPoints)
{
    if(pPoints.length === 0)
        return { min: BABYLON.Vector3.Zero(), max: BABYLON.Vector3.Zero() };

    let vecMin = pPoints[0].clone();
    let vecMax = pPoints[0].clone();
    for(let idx = 1; idx < pPoints.length; idx++)
    {
        if(pPoints[idx].x < vecMin.x)
            vecMin.x = pPoints[idx].x;
        if(pPoints[idx].y < vecMin.y)
        	vecMin.y = pPoints[idx].y;
        if(pPoints[idx].z < vecMin.z)
        	vecMin.z = pPoints[idx].z;

        if(pPoints[idx].x > vecMax.x)
            vecMax.x = pPoints[idx].x;
        if(pPoints[idx].y > vecMax.y)
        	vecMax.y = pPoints[idx].y;
        if(pPoints[idx].z > vecMax.z)
        	vecMax.z = pPoints[idx].z;
    }

    return { 
		min: vecMin, max: vecMax,

        // TODO: We should really use these everywhere to match BJS, but for backwards
        // compat, the above are also included.
        minimumWorld: vecMin, maximumWorld: vecMax,
	};
}
/*****************************************************************************/
static rotateMeshBasic(mesh, axisNormal, fDegRotation)
{
	let rotationAmount = fDegRotation / 180.0 * Math.PI;

	mesh.rotate(axisNormal, rotationAmount, BABYLON.Space.WORLD);

	let quatRotation = BABYLON.Quaternion.Inverse(mesh.pickedObjectInitRotation).multiply(mesh.rotationQuaternion);
	mesh.accumOnlyQuaternion = mesh.pickedObjectInitAccumQuaternion.multiply(quatRotation);
}
/*****************************************************************************/
static restoreSubmeshes(pSubmeshes, pDestMesh)
{
	pDestMesh.releaseSubMeshes();
	for(let iSubmeshIndex = 0; iSubmeshIndex < pSubmeshes.length; iSubmeshIndex++)
	{
		let srcSubMesh = pSubmeshes[iSubmeshIndex];
		let newSubmesh = new BABYLON.SubMesh(srcSubMesh.materialIndex, srcSubMesh.verticesStart, srcSubMesh.verticesCount,
												srcSubMesh.indexStart, srcSubMesh.indexCount, pDestMesh);

		newSubmesh.origName = srcSubMesh.origName;
		newSubmesh.group = srcSubMesh.group;
	}
}
/*****************************************************************************/
static pickMesh(fPtX, fPtY, scene, conditionFunction)
{
	let pickResult = Utils3D.pick(fPtX, fPtY, scene, conditionFunction);

	// Set the picked result, except if it's our ground.
	let newPickedObject = pickResult.pickedMesh;
	if(newPickedObject && newPickedObject.isGround)
		newPickedObject = null;
	return newPickedObject;
}
/*****************************************************************************/
static isMeshMultiMesh(mesh)
{
	return mesh.subMeshes && mesh.subMeshes.length > 1;
}
/*****************************************************************************/
static fixIncorrectFaceIdIndex(pickResult)
{
	// Workaround for yet another bug: when the mesh contains multiple submeshes,
	// the face id returned is wrong - it's relative to the start of the indices
	// in the picked submesh, yet other Babylon code treats it as if it were
	// an absolute face index. So we just overwrite it with the correct value
	// ourselves:
	if(pickResult && pickResult.pickedMesh)
	{
		let pSubmesh = Utils3D.getSubmeshById(pickResult.pickedMesh, pickResult.subMeshId);
		if(pSubmesh)
			pickResult.faceId = pSubmesh.indexStart/3 + pickResult.faceId;
	}
}
/*****************************************************************************/
static pick(fPtX, fPtY, scene, conditionFunction)
{
	let pickResult = scene.pick(fPtX, fPtY, conditionFunction, false);
	Utils3D.fixIncorrectFaceIdIndex(pickResult);
	return pickResult;
}
/*****************************************************************************/
static getEpsilon()
{
	if(BABYLON.Engine.Epsilon)
		return BABYLON.Engine.Epsilon;
	else if(BABYLON.Epsilon)
		return BABYLON.Epsilon;

	return 0.001;
}
/*****************************************************************************/
static getMeshesChildren(objectList, bAlwaysIncludeSelf = false, conditionFunction = null)
{
	if(!objectList)
		objectList = [];

	if(!Array.isArray(objectList))
		objectList = [objectList];

	let pResMeshes = [];
	objectList.forEach((pMesh) =>
	{
		if(!pMesh || !pMesh.getChildren)
			return;

		if(conditionFunction && !conditionFunction(pMesh))
			return;

		let pChildren = pMesh.getChildren();
		if(pChildren.length > 0)
			pResMeshes = pResMeshes.concat(Utils3D.getMeshesChildren(pChildren, false, conditionFunction));
		
		if(pChildren.length === 0 || bAlwaysIncludeSelf)
			pResMeshes.push(pMesh);
	});

	return pResMeshes;
}
/*****************************************************************************/
static getObjectsBBoxWithChildren(objectList)
{
	let flatMeshesList = Utils3D.getMeshesChildren(objectList);
	return Utils3D.getObjectsBBox(flatMeshesList);
}
/*****************************************************************************/
static getMeshByUniqueId(id, scene)
{
	for(let pMesh of scene.meshes)
	{
		if(pMesh.uniqueId === id)
			return pMesh;
	}

	return null;
}
/*****************************************************************************/
static getObjectsBBox(objectList)
{
	let bbox;
	let resBBox = new BABYLON.BoundingBox(
		new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE), 
		new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE));
	for(let index = 0; index < objectList.length; index++)
	{
		let m = objectList[index];
        if(!m)
            continue;

		if(m.computeWorldMatrix)
		{
			m.computeWorldMatrix(true);
			bbox = m.getBoundingInfo().boundingBox;
		}
		else
			bbox = m.getBoundingBox();
		
		if(bbox.minimumWorld.x < resBBox.minimumWorld.x)
			resBBox.minimumWorld.x = bbox.minimumWorld.x;
		if(bbox.minimumWorld.y < resBBox.minimumWorld.y)
			resBBox.minimumWorld.y = bbox.minimumWorld.y;
		if(bbox.minimumWorld.z < resBBox.minimumWorld.z)
			resBBox.minimumWorld.z = bbox.minimumWorld.z;

		if(bbox.maximumWorld.x > resBBox.maximumWorld.x)
			resBBox.maximumWorld.x = bbox.maximumWorld.x;
		if(bbox.maximumWorld.y > resBBox.maximumWorld.y)
			resBBox.maximumWorld.y = bbox.maximumWorld.y;
		if(bbox.maximumWorld.z > resBBox.maximumWorld.z)
			resBBox.maximumWorld.z = bbox.maximumWorld.z;
	}

	resBBox.vectorsWorld[0] = new BABYLON.Vector3(resBBox.minimumWorld.x, resBBox.minimumWorld.y, resBBox.minimumWorld.z);
	resBBox.vectorsWorld[1] = new BABYLON.Vector3(resBBox.maximumWorld.x, resBBox.maximumWorld.y, resBBox.maximumWorld.z);

	return resBBox;
}
/*****************************************************************************/
static isAxisAligned(vecDirection)
{
    let delta = Utils3D.getEpsilon();
    let axes = [new BABYLON.Vector3(1, 0, 0), new BABYLON.Vector3(0, 1, 0), new BABYLON.Vector3(0, 0, 1)];
    for(let axis of axes)
	{
        let isAligned = (1.0 - Math.abs(BABYLON.Vector3.Dot(vecDirection, axis))) < delta;
        if(isAligned)
			return true;
    }

    return false;
}
/*****************************************************************************/
// Computes dot product of the normal (vecNormal) with camera ray to the point (vecPoint).
// It is very handy for deciding visibility of the translate/scaling widgets,
// Because in extreme cases, camera ray and the plane intersect weird places that cannot be processed.
static dotNormalWithCameraLook(vecNormal, vecPoint, pCamera)
{
	let vecCamPos = pCamera.position;
	let camToPoint = vecPoint.subtract(vecCamPos);
	camToPoint.normalize();

	return BABYLON.Vector3.Dot(vecNormal, camToPoint );
}
/*****************************************************************************/
// Computes winding state of a 2D polygon: CW or CCW. If polygon has 3D vertices,
// it is projected to the XZ plane (Y ignored).
//
// Input:
//	- vecPoints: array of objects with 'x','y' or 'x','y','z' properties, or array of
// 				 [x,y] or [x,y,z] arrays. If the input is 3D, the polygon is assumed to
//				 be lying on the XZ plane (i.e. the Y coordinates are ignored).
//				 Array of BABYLON.Vector2/3 can also be used.
// Output: UIGlobals.OrientationType.OrientationCCW or UIGlobals.OrientationType.OrientationCW
//
// Difference between 2D and 3D case:
//       ^ Y
// 2D:   |   4<--3
//       |   v   ^
//       |   1-->2
//       +--------> X
//
// 3D:   in the 3D case, we're on the XZ plane
//       so to meaningfully determine winding,
//       we need to rotate that polygon onto the XY-
//       plane. Imagine taking that polygon and rotating
//       it 90 degrees around the X axis from XZ to XY
//       such that the polygon lies on the negative side
//       of the Y-axis (if you'd rotate it the other way,
//       the winding would be reversed)
//       To achieve this, you need to remap the coordinates
//       from 3D to 2D as follows:
//          x: = x
//          y: = -z
//
//       +--------> X            +--------> X
//       |   2<--1               |    2<--1
//       |   v   ^       ==>     |    v   ^
//       |   3-->4               |    3<--4
//       v Z                     v
//                              -Y
//
static getPolygonOrientation(vecPoints)
{
	if(vecPoints.length && vecPoints[0] instanceof Array) {
		if(vecPoints[0].length === 2) {
			// 2D
			vecPoints = vecPoints.map(p => {
				return {x: p[0], y: p[1]};
			});
		} else {
			vecPoints = vecPoints.map(p => {
				return {x: p[0], y: 0, z: p[2]};
			});
		}
	}

	// Handle edge cases
	if(vecPoints.length <= 2)
		return UIGlobals.OrientationType.OrientationCCW;

	// input may be 2D instead of 3D
	// in case of 3D, we interpret the polygon as lying in the
	// XZ plane and we need to project its coordinates to
	// the 2D plane (XY) while preserving its winding, hence
	// the -1 multiplier
	const secondCoord = (vecPoints[0].z) ? 'z' : 'y';
	const secondCoordSign = (vecPoints[0].z) ? -1 : 1;

    let i,j;
    let d = 0.0;

    let iNumPoints = vecPoints.length;
    for(i = 0; i < iNumPoints; i++)
    {
        j = (i + 1) % iNumPoints;
        let x1 = vecPoints[i].x;
        let y1 = secondCoordSign * vecPoints[i][secondCoord];
        let x2 = vecPoints[j].x;
        let y2 = secondCoordSign * vecPoints[j][secondCoord];
        d += (x2 - x1)*(y2 + y1);
    }
    if(d >= 0)
        return UIGlobals.OrientationType.OrientationCW;
    else
        return UIGlobals.OrientationType.OrientationCCW;
};
/*****************************************************************************/
// Convenience wrappper for getPolygonOrientation()
static isPolygonCCW(vecPoints) {
	return Utils3D.getPolygonOrientation(vecPoints) === UIGlobals.OrientationType.OrientationCCW;
};
/*****************************************************************************/
static getLinePlaneIntersection(vecLineStart, vecLineEnd, vecPlaneNormal, fPlaneOffset)
{
	let vecDir = vecLineEnd.subtract(vecLineStart);
	let nDotA = BABYLON.Vector3.Dot(vecPlaneNormal, vecLineStart);
	let nDotBA = BABYLON.Vector3.Dot(vecPlaneNormal, vecDir);

	if(Math.abs(nDotBA) < 1e-6)
		return null;

	return vecDir.scale((fPlaneOffset - nDotA)/nDotBA).add(vecLineStart);
}
/*****************************************************************************/
static getMeshOrSubmeshByName(meshName, scene)
{
	let mesh = null;
	for(let idx = 0; idx < scene.meshes.length; idx++)
	{
		mesh = scene.meshes[idx];
		if(mesh.name === meshName)
			return mesh;

		// Otherwise, try to find meshes by sub-materials or sub-meshes directly.
		if(!mesh.subMeshes || !mesh.material || !mesh.material.subMaterials)
			continue;

		for(let index = 0; index < mesh.subMeshes.length; index++)
		{
			let subMesh = mesh.subMeshes[index];
			let subMaterial = mesh.material.subMaterials[subMesh.materialIndex];
			if(
				(subMaterial && ((subMaterial.name === meshName) || ((subMaterial.origName) && (subMaterial.origName === meshName))))
				|| (subMesh && ('group' in subMesh) && (subMesh.group === meshName))
			) {
				return { mesh: mesh, submeshIndex: index };
			}
		}
	}
	return null;
}
/*****************************************************************************/
static makeMeshUpdatable(pMesh, attributesToMakeUpdatable)
{
	if(!attributesToMakeUpdatable)
		attributesToMakeUpdatable = [BABYLON.VertexBuffer.PositionKind, BABYLON.VertexBuffer.NormalKind];

	let bIsUpdatable = true;
	for(let attribute of attributesToMakeUpdatable)
		bIsUpdatable &= pMesh.geometry.getVertexBuffer(attribute).isUpdatable();

	if(bIsUpdatable)
		return;

	// Setting vertex data deletes submeshes; however, we cannot call update only, because
	// initially the mesh is loaded as non-updatable (and this is hard-coded into the
	// Babylon file loader, unfortunately). So instead, we set the vertices the first time,
	// telling the mesh to be updatable, and after that, we only make the update call.
	// Credit to Mert for finding out a solution to preserve submeshes.
	let subMeshesBackup = pMesh.subMeshes.slice();
	for(let attribute of attributesToMakeUpdatable)
	{
		let attributeData = pMesh.getVerticesData(attribute).slice(0);
		pMesh.setVerticesData(attribute, attributeData, attribute === BABYLON.VertexBuffer.PositionKind);
	}
	Utils3D.restoreSubmeshes(subMeshesBackup, pMesh);
}
/*****************************************************************************/
static computeTightWorldBBox(mesh)
{
	// The reason for this function's existence is because Babylon "cheats" when
	// computing the world-space bbox. It first computes an OBB (oriented bbox)
	// in local space, then takes its axes, converts them to world space, and puts
	// a bounding box around that.
	// This, technically, is *a* world-space bbox; however, it is a loose one, meaning
	// there's a smaller bbox that exists that still encompasses all vertices.
	// To do physics accurately, we kind of need this one.

	mesh.computeWorldMatrix(true);
	let world = mesh.getWorldMatrix();
	let positions = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
	const count = mesh.getTotalVertices();

	let minimum = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
	let maximum = new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);
	let current = BABYLON.Vector3.Zero();
	let currentLocal = BABYLON.Vector3.Zero();
	for(let index = 0; index < count; index++)
	{
		currentLocal.x = positions[index*3];
		currentLocal.y = positions[index*3 + 1];
		currentLocal.z = positions[index*3 + 2];

		// Transform to world:
		BABYLON.Vector3.TransformCoordinatesToRef(currentLocal, world, current);

		minimum = BABYLON.Vector3.Minimize(current, minimum);
		maximum = BABYLON.Vector3.Maximize(current, maximum);
	}

	return { minimumWorld: minimum, maximumWorld: maximum	};
}
/*****************************************************************************/
static getMeshWorldTransforms(pMesh)
{
	pMesh.computeWorldMatrix(true);
	let cumulMatrix = pMesh.getWorldMatrix().clone();

	let vecScale = new BABYLON.Vector3();
	let vecTranslations = new BABYLON.Vector3();
	let quatRotation = new BABYLON.Quaternion();
	cumulMatrix.decompose(vecScale, quatRotation, vecTranslations);

	return { scale: vecScale, translation: vecTranslations, quaternion: quatRotation };
}
/*****************************************************************************/
static bakePivotTransform(pMesh, bAllowVertexTransformBaking = true)
{
	if(pMesh.getPivotMatrix().isIdentity())
		return;

	let oldScale = pMesh.scaling.clone();
	let oldRotations = pMesh.rotation.clone();
	let oldPosition = pMesh.position.clone();
	
	// We have to do this because it seems there's might be ambiguity in the
	// matrix decomposition with negative scales that results in incorrect rotations.
	if(bAllowVertexTransformBaking)
	if(oldScale.x < 0 || oldScale.y < 0 || oldScale.z < 0)
	{
		pMesh.position = BABYLON.Vector3.Zero();
		pMesh.rotation = BABYLON.Vector3.Zero();

		pMesh.scaling = new BABYLON.Vector3(1, 1, 1);
		if(oldScale.x < 0)
			pMesh.scaling.x *= -1;
		if(oldScale.y < 0)
			pMesh.scaling.y *= -1;
		if(oldScale.z < 0)
			pMesh.scaling.z *= -1;

		pMesh.makeGeometryUnique();
		pMesh.bakeCurrentTransformIntoVertices();

		pMesh.position = oldPosition;
		pMesh.rotation = oldRotations;
		pMesh.scaling = new BABYLON.Vector3(Math.abs(oldScale.x), Math.abs(oldScale.y), Math.abs(oldScale.z));
	}

	pMesh.computeWorldMatrix(true);
	let currWorldMatrix = pMesh.getWorldMatrix().clone();

	let scale = new BABYLON.Vector3();
	let trans = new BABYLON.Vector3();
	let rot = new BABYLON.Quaternion();
	currWorldMatrix.decompose(scale, rot, trans);
	pMesh.setPivotMatrix(BABYLON.Matrix.Identity());

	pMesh.position = trans;
	pMesh.rotation = rot.toEulerAngles();
	pMesh.scaling = scale;

	pMesh.computeWorldMatrix(true);
}
/*****************************************************************************/
static getSignedPerpDistanceTo(vecLineStart, vecLineEnd, vecPoint)
{
	let vecDir = vecLineEnd.subtract(vecLineStart).normalize();
	let vecNormal = new BABYLON.Vector3(-vecDir.z, vecDir.y, vecDir.x);

	return BABYLON.Vector3.Dot(vecPoint.subtract(vecLineStart), vecNormal);
}
/*****************************************************************************/
static computeBBoxMidPoints(pMesh)
{
	let xAxis = new BABYLON.Vector3(1, 0, 0);
	let zAxis = new BABYLON.Vector3(0, 0, 1);

	let pBBox = pMesh.getBoundingInfo().boundingBox;
	let vecSize = pBBox.maximumWorld.subtract(pBBox.minimumWorld).scale(0.5);

	// Note that we ignore the prop rotation
	let pBoxMidPoints = [];
	pBoxMidPoints[0] = pMesh.position.add(xAxis.scale(vecSize.x));
	pBoxMidPoints[1] = pMesh.position.add(xAxis.scale(-vecSize.x));
	pBoxMidPoints[2] = pMesh.position.add(zAxis.scale(vecSize.z));
	pBoxMidPoints[3] = pMesh.position.add(zAxis.scale(-vecSize.z));

	return pBoxMidPoints;
}
/*****************************************************************************/
static computeBBoxCornerPoints(pMesh)
{
	let xAxis = new BABYLON.Vector3(1, 0, 0);
	let zAxis = new BABYLON.Vector3(0, 0, 1);

	let pBBox = pMesh.getBoundingInfo().boundingBox;
	let vecSize = pBBox.maximumWorld.subtract(pBBox.minimumWorld).scale(0.5);

	let pCorners = [];
	pCorners[0] = pMesh.position.add(xAxis.scale(-vecSize.x)).add(zAxis.scale(-vecSize.z));
	pCorners[1] = pMesh.position.add(xAxis.scale(-vecSize.x)).add(zAxis.scale(vecSize.z));
	pCorners[2] = pMesh.position.add(xAxis.scale(vecSize.x)).add(zAxis.scale(vecSize.z));
	pCorners[3] = pMesh.position.add(xAxis.scale(vecSize.x)).add(zAxis.scale(-vecSize.z));

	return pCorners;
}
/*****************************************************************************/
static doesBBoxContain(vecOuterMin, vecOuterMax, vecInnerMin, vecInnerMax)
{
	return (vecInnerMin.x >= vecOuterMin.x && vecInnerMin.y >= vecOuterMin.y && vecInnerMin.z >= vecOuterMin.z
		&& vecInnerMax.x <= vecOuterMax.x && vecInnerMax.y <= vecOuterMax.y && vecInnerMax.z <= vecOuterMax.z);
}
/*****************************************************************************/
static doBBoxesIntersect(vecMinA, vecMaxA, vecMinB, vecMaxB)
{
    let aLeftOfB = vecMaxA.x < vecMinB.x;
    let aRightOfB = vecMinA.x > vecMaxB.x;
    let aAboveB = vecMinA.y > vecMaxB.y;
    let aBelowB = vecMaxA.y < vecMinB.y;
    let aBehindB = vecMinA.z > vecMaxB.z;
    let aInfrontB = vecMaxA.z < vecMinB.z;

    return !(aLeftOfB || aRightOfB || aAboveB || aBelowB || aBehindB || aInfrontB);
}
/*****************************************************************************/
static computeRotatedBBoxCornerPoints(bboxMin, bboxMax, fRotAngleInDegrees = 0) {
    return Utils3D.getRotatedBBoxCorners(bboxMin, bboxMax, fRotAngleInDegrees);
}
/*****************************************************************************/
static computeRotatedBBoxAllCorners(bboxMin, bboxMax, fRotAngleInDegrees = 0) {
    return Utils3D.getRotatedBBoxCorners(bboxMin, bboxMax, fRotAngleInDegrees, false);
}
/*****************************************************************************/
static getRotatedBBoxCorners(bboxMin, bboxMax, fRotAngleInDegrees = 0, bottomOnly = true)
{
	let vecCenter = bboxMax.add(bboxMin).scale(0.5);
	let matInvTranslate = BABYLON.Matrix.Translation(-vecCenter.x, -vecCenter.y, -vecCenter.z);
	let matRotate = BABYLON.Matrix.RotationY(fRotAngleInDegrees/180.0*Math.PI);
	let matTranslate = BABYLON.Matrix.Translation(vecCenter.x, vecCenter.y, vecCenter.z);
	let matFinal = matInvTranslate.multiply(matRotate).multiply(matTranslate);

	let vecTemp3D;
	let corners3D = [];
	// Order: left edge, top edge, right edge, (implicit) bottom edge
	// Please do not change as other code relies on this.
	vecTemp3D = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bboxMin.x, bboxMin.y, bboxMin.z), matFinal);
	corners3D.push(vecTemp3D);

	vecTemp3D = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bboxMin.x, bboxMin.y, bboxMax.z), matFinal);
	corners3D.push(vecTemp3D);

	vecTemp3D = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bboxMax.x, bboxMin.y, bboxMax.z), matFinal);
	corners3D.push(vecTemp3D);

	vecTemp3D = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bboxMax.x, bboxMin.y, bboxMin.z), matFinal);
    corners3D.push(vecTemp3D);

    if(!bottomOnly) {
        vecTemp3D = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bboxMin.x, bboxMax.y, bboxMin.z), matFinal);
        corners3D.push(vecTemp3D);

        vecTemp3D = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bboxMin.x, bboxMax.y, bboxMax.z), matFinal);
        corners3D.push(vecTemp3D);

        vecTemp3D = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bboxMax.x, bboxMax.y, bboxMax.z), matFinal);
        corners3D.push(vecTemp3D);

        vecTemp3D = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(bboxMax.x, bboxMax.y, bboxMin.z), matFinal);
        corners3D.push(vecTemp3D);
    }

	return corners3D;
}
/*****************************************************************************/
static getSubmeshById(pMesh, iSubmeshId)
{
	for(let pSubmesh of pMesh.subMeshes)
	{
		if(pSubmesh._id == iSubmeshId)
			return pSubmesh;
	}
	return null;
}
/*****************************************************************************/
static isAxisVertical(axis, isYVertical) {
	const axisNormalized = BABYLON.Vector3.Normalize(axis);
	if(isYVertical) {
		return (BABYLON.Vector3.Cross(axisNormalized, BABYLON.Axis.Y).length() < 0.1);
	}
	return (BABYLON.Vector3.Cross(axisNormalized, BABYLON.Axis.Z).length() < 0.1);
}
/*****************************************************************************/
static rotatePointAroundAxis(vecAxis, fRadAngle, vecPoint)
{
	let vecPointOut = new BABYLON.Vector3();
	Utils3D.rotatePointAroundAxisToRef(vecAxis, fRadAngle, vecPoint, vecPointOut)
	return vecPointOut;
}
/*****************************************************************************/
static rotatePointAroundAxisToRef(vecAxis, fRadAngle, vecPointIn, vecPointOut)
{
	if(!_cache_rotPoint_Quat)
	{
		_cache_rotPoint_Quat = new BABYLON.Quaternion();
		_cache_rotPoint_Mat = new BABYLON.Matrix.Identity();
	}

	BABYLON.Quaternion.RotationAxisToRef(vecAxis, fRadAngle, _cache_rotPoint_Quat);
	_cache_rotPoint_Quat.toRotationMatrix(_cache_rotPoint_Mat);

	BABYLON.Vector3.TransformCoordinatesToRef(vecPointIn, _cache_rotPoint_Mat, vecPointOut);
}
/*****************************************************************************/
static doRectanglesIntersect(vecPlaneCenter, vecPlaneNormal, vecPlaneX, vecPlaneY, rect1Corners, rect2Corners)
{
	// Project all corners onto the 2D plane, then intersect in 2D
	let projCorners1 = [];
	let projCorners2 = [];
	let vecDiff = BABYLON.Vector3.Zero();
	for(let vecPoint of rect1Corners)
	{
		vecPoint.subtractToRef(vecPlaneCenter, vecDiff);
		let vecProjPoint = new BABYLON.Vector2(BABYLON.Vector3.Dot(vecDiff, vecPlaneX), BABYLON.Vector3.Dot(vecDiff, vecPlaneY));
		projCorners1.push(vecProjPoint);
	}

	for(let vecPoint of rect2Corners)
	{
		vecPoint.subtractToRef(vecPlaneCenter, vecDiff);
		let vecProjPoint = new BABYLON.Vector2(BABYLON.Vector3.Dot(vecDiff, vecPlaneX), BABYLON.Vector3.Dot(vecDiff, vecPlaneY));
		projCorners2.push(vecProjPoint);
	}

	let bbox1 = Utils2D.computePointsBBox(projCorners1);
	let bbox2 = Utils2D.computePointsBBox(projCorners2);

	return Utils2D.doRectanglesIntersect(bbox1.min, bbox1.max, bbox2.min, bbox2.max);
}
/*****************************************************************************/
static getPerpDistanceToPoint(vecStart, vecDir, vecPoint)
{
	let vecDiff1 = memCacher.getVector3(CACHE_IDS.VEC3_DIFF1);
	vecPoint.subtractToRef(vecStart, vecDiff1);

	let fDist1 = Vector3.Dot(vecDiff1, vecDir);
	vecDir.scaleToRef(fDist1, vecDiff1);
	vecDir = vecDiff1;
	
	let vecPoint1 = memCacher.getVector3(CACHE_IDS.VEC3_POINT1);
	vecStart.addToRef(vecDir, vecPoint1);
	
	let vecPoint2 = memCacher.getVector3(CACHE_IDS.VEC3_POINT2);
	vecPoint1.subtractToRef(vecPoint, vecPoint2);

	return vecPoint2.length();	
}
/*****************************************************************************/
static getDistancePointToSegment(vecStart, vecDir, fLength, vecPoint)
{
	let fPerpDistance = Math.abs(Utils3D.getPerpDistanceToPoint(vecStart, vecDir, vecPoint));

	// We must also take into account distances to its end points:
	let toPoint = vecPoint.subtract(vecStart);
	let fDistAlong = BABYLON.Vector3.Dot(vecDir, toPoint);

	if(fDistAlong >= 0 && fDistAlong <= fLength)
		fDistAlong = 0;
	else if(fDistAlong < 0)
		fDistAlong = Math.abs(fDistAlong);
	else if(fDistAlong > fLength)
		fDistAlong -= fLength;

	return Math.max(fDistAlong, fPerpDistance);
}
/*****************************************************************************/
static doesSegmentContainPoint(vecStart1, vecDir, fLen, vecPoint, fEpsilon = 0.001)
{
	// Point must be within epislon of the normal distance
	let vecDiff1 = memCacher.getVector3(CACHE_IDS.VEC3_DIFF1);
	vecPoint.subtractToRef(vecStart1, vecDiff1);

	let fDist1 = Vector3.Dot(vecDiff1, vecDir);
	vecDir.scaleToRef(fDist1, vecDiff1);
	vecDir = vecDiff1;
	
	let vecPoint1 = memCacher.getVector3(CACHE_IDS.VEC3_POINT1);
	vecStart1.addToRef(vecDir, vecPoint1);
	
	let vecPoint2 = memCacher.getVector3(CACHE_IDS.VEC3_POINT2);
	vecPoint1.subtractToRef(vecPoint, vecPoint2);

	let fPerpDist1 = vecPoint2.lengthSquared();
	if(fPerpDist1 > Math.abs(fEpsilon*fEpsilon))
		return false;

	// Now, the distance along the curve must also be within segment one dist:
	if(fDist1 < -fEpsilon || fDist1 > fLen + fEpsilon)
		return false;

	return true;
}
/*****************************************************************************/
// Note: this can be and is used for 2D points, as well.
static doesSegmentContain(vecStart1, vecEnd1, vecStart2, vecEnd2, fOptEpsilon = undefined, fPerpEpsilon = undefined)
{
	let vecDir = vecEnd1.subtract(vecStart1);
	let fLen = vecDir.length();
	vecDir.normalize();

	// Both points must be within epislon of the normal distance
	const EPSILON = (fOptEpsilon === undefined) ? Utils3D.getEpsilon() : fOptEpsilon;
	const PERP_EPSILON = (fPerpEpsilon === undefined) ? EPSILON : fPerpEpsilon;
	let vecDiff1 = vecStart2.subtract(vecStart1);
	let vecDiff2 = vecEnd2.subtract(vecStart1);

	let funcDotProd = vecStart1.z === undefined ? BABYLON.Vector2.Dot : BABYLON.Vector3.Dot;
	let fDist1 = funcDotProd(vecDiff1, vecDir);
	let fDist2 = funcDotProd(vecDiff2, vecDir);

	let fPerpDist1 = vecStart1.add(vecDir.scale(fDist1)).subtract(vecStart2).length();
	let fPerpDist2 = vecStart1.add(vecDir.scale(fDist2)).subtract(vecEnd2).length();
	if(fPerpDist1 > PERP_EPSILON || fPerpDist2 > PERP_EPSILON)
		return false;

	// Now, the distance along the curve must also be within segment one dist:
	if(fDist1 < -EPSILON || fDist1 > fLen + EPSILON)
		return false;
	
	if(fDist2 < -EPSILON || fDist2 > fLen + EPSILON)
		return false;

	// Test directions for larger perp epsilons
	if(fPerpEpsilon !== undefined)
	{
		let vecDir2 = vecEnd2.subtract(vecStart2);
		vecDir2.normalize();

		if(Math.abs(Math.abs(funcDotProd(vecDir, vecDir2)) - 1.0) > EPSILON)
			return false;
	}

	return true;
}
/*****************************************************************************/
static isPointInTriangle(vecPoint, vecTriangle, bIgnorePerpDistance = false, optEpsilon = 0, perpEpsilon = 1e-4)
{
    let faceSide1 = memCacher.getVector3(CACHE_IDS.VEC3_SIDE1);
    let faceSide2 = memCacher.getVector3(CACHE_IDS.VEC3_SIDE2);
    let faceNormal = memCacher.getVector3(CACHE_IDS.VEC3_NORMAL);

    vecTriangle[1].subtractToRef(vecTriangle[0], faceSide1);
    vecTriangle[2].subtractToRef(vecTriangle[0], faceSide2);
	BABYLON.Vector3.CrossToRef(faceSide1, faceSide2, faceNormal);
	faceNormal.normalize();

	let vecToOrigin = memCacher.getVector3(CACHE_IDS.VEC3_DIFF);
	vecPoint.subtractToRef(vecTriangle[0], vecToOrigin);
	if(!bIgnorePerpDistance && Math.abs(Vector3.Dot(vecToOrigin, faceNormal)) > perpEpsilon)
		return false;

	let dot00 = Vector3.Dot(faceSide1, faceSide1);
	let dot01 = Vector3.Dot(faceSide1, faceSide2);
	let dot02 = Vector3.Dot(faceSide1, vecToOrigin);
	let dot11 = Vector3.Dot(faceSide2, faceSide2);
	let dot12 = Vector3.Dot(faceSide2, vecToOrigin);

	// Compute barycentric coordinates
	let invDenom = 1.0 / (dot00 * dot11 - dot01 * dot01);
	let u = (dot11 * dot02 - dot01 * dot12) * invDenom;
	let v = (dot00 * dot12 - dot01 * dot02) * invDenom;
	// Check if point is in triangle
	return (u >= optEpsilon) && (v >= optEpsilon) && (u + v <= 1 - optEpsilon);	
}
/*****************************************************************************/
static debugVisualizePlane(plane, scene, size = 5, color = null)
{
    const normal = plane.normal.clone();
	normal.normalize();

    // Create a plane mesh
    const planeMesh = BABYLON.MeshBuilder.CreatePlane("visualPlane", { size, sideOrientation: BABYLON.Mesh.DOUBLESIDE, }, scene);

    // Calculate a point on the plane
    const pointOnPlane = normal.scale(-plane.d);

    // Set the plane position
    planeMesh.position = pointOnPlane;

    // Align the plane to the normal direction
    const up = new BABYLON.Vector3(0, 1, 0);
    if (BABYLON.Vector3.Dot(up, normal) > 0.99) {
        // If normal is almost up, use a different reference vector
        up.set(1, 0, 0);
    }

    planeMesh.rotationQuaternion = Utils3D.directionToQuaternion(normal);

    // Set material and color
    const material = new BABYLON.StandardMaterial("planeMaterial", scene);
    material.diffuseColor = color ? color : BABYLON.Color3.Green();
	material.emissiveColor = material.diffuseColor.scale(0.25);
    material.alpha = 0.5; // Make it semi-transparent for better visualization
    planeMesh.material = material;

    return planeMesh;
}
/*****************************************************************************/
static debugShowNormals(mesh, scene, size = 0.25)
{
    let normals = mesh.getVerticesData(BABYLON.VertexBuffer.NormalKind);
    let positions = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
    let color = BABYLON.Color3.Red();

    let lines = [];
    for (let i = 0; i < normals.length; i += 3) {
        let v1 = BABYLON.Vector3.FromArray(positions, i);
        let v2 = v1.add(BABYLON.Vector3.FromArray(normals, i).scaleInPlace(size));
        lines.push([v1.add(mesh.position), v2.add(mesh.position)]);
    }
    let normalLines = BABYLON.MeshBuilder.CreateLineSystem("normalLines", {lines: lines}, scene);
    normalLines.color = color;
    return normalLines;
}
/*****************************************************************************/
static debugShowLines(lines, scene, color = null)
{
	if(!color)
		color = BABYLON.Color3.Red();

    let normalLines = BABYLON.MeshBuilder.CreateLineSystem("normalLines", {lines: lines}, scene);
    normalLines.color = color;
	normalLines.visOnly = true;
    return normalLines;
}
/*****************************************************************************/
static createDebugMarker(vecPos, fSize = 0.1, color = null, fOpacity = 1.0)
{
	if(!color)
		color = BABYLON.Color3.Blue();

	let pBox = BABYLON.MeshBuilder.CreateBox('debugMarker', { 
		width: fSize, 
		depth: fSize, 
		height: fSize, 
		sideOrientation: BABYLON.Mesh.FRONTSIDE,
		updatable: false
	}, BABYLON.Engine.LastCreatedScene);

	pBox.position = vecPos.clone();
	pBox.material = new BABYLON.StandardMaterial('debugMarkerMaterial', BABYLON.Engine.LastCreatedScene);
	pBox.visibility = fOpacity;
	pBox.material.diffuseColor = color.clone();
	pBox.material.emissiveColor = pBox.material.diffuseColor.scale(0.1);
	pBox.material.specularColor = new BABYLON.Color3(0, 0, 0);
	return pBox;
}
/*****************************************************************************/
static getAngleBetweenVectorsOnPlane(vec1, vec2, vecNormal)
{
	let right = BABYLON.Vector3.Cross(vecNormal, vec1);
	let forward = BABYLON.Vector3.Cross(right, vecNormal);

	let projectedVec1 = new BABYLON.Vector2(BABYLON.Vector3.Dot(vec1, right), BABYLON.Vector3.Dot(vec1, forward));
	let projectedVec2 = new BABYLON.Vector2(BABYLON.Vector3.Dot(vec2, right), BABYLON.Vector3.Dot(vec2, forward));

	projectedVec1.normalize();
	projectedVec2.normalize();
	return Math.atan2((projectedVec1.x * projectedVec2.y - projectedVec1.y * projectedVec2.x), BABYLON.Vector2.Dot(projectedVec1, projectedVec2))/Math.PI*180.0;
}
/*****************************************************************************/
static getMeshVertexGlobalPositions(pSrcMesh)
{
	let vecLocalPointPosition = BABYLON.Vector3.Zero();

	let globalPositions = [];

	pSrcMesh.computeWorldMatrix(true);
	let currWorldMatrix = pSrcMesh.getWorldMatrix();

	let positions = pSrcMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
	for(let pointIndex = 0; pointIndex < positions.length; pointIndex += 3)
	{
		vecLocalPointPosition.copyFromFloats(positions[pointIndex], positions[pointIndex + 1], positions[pointIndex + 2]);
		let vecGlobalPos = BABYLON.Vector3.TransformCoordinates(vecLocalPointPosition, currWorldMatrix);
		globalPositions.push({pos: vecGlobalPos, index: pointIndex});
	}

	return globalPositions;
}
/*****************************************************************************/
static getMeshesGlobalVertexPositions(meshList)
{
	let vertexPositions = [];

	for(let mesh of meshList)
		vertexPositions = vertexPositions.concat(Utils3D.getMeshVertexGlobalPositions(mesh));

	let id = 0;
	for(let info of vertexPositions)
	{
		info.uniqueId = id;
		id++;
	}		

	return vertexPositions;
}
/*****************************************************************************/
static rayLineIntersection(vecStart, vecDir, vecSegmentStart, vecSegmentEnd, fOptTolerance = 1e-4)
{
	let testRay = new BABYLON.Ray(vecStart, vecDir);
	let fIntersectDist = testRay.intersectionSegment(vecSegmentStart, vecSegmentEnd, fOptTolerance);

	let vecOtherDir = vecSegmentEnd.subtract(vecSegmentStart);
	let otherLen = vecOtherDir.length();
	vecOtherDir.normalize();

	let dSecondLineIntersect = Number.MAX_VALUE;
	if(fIntersectDist > -1)
	{
		let point = vecStart.add(vecDir.scale(fIntersectDist));
		let fromOtherStart = point.subtract(vecSegmentStart);
		dSecondLineIntersect = BABYLON.Vector3.Dot(fromOtherStart, vecOtherDir);

		if(dSecondLineIntersect <= fOptTolerance || dSecondLineIntersect > otherLen - fOptTolerance)
			fIntersectDist = -1;
	}
	
	return { 
		dIntersect: fIntersectDist,
		otherDir: vecOtherDir,
		dSecondLineIntersect: dSecondLineIntersect,
	};
}
/*****************************************************************************/
static rayBoxIntersection(origin, direction, bboxMin, bboxMax)
{ 
	// fast ray-box intersection
	let fNear = -Number.MAX_VALUE;
	let fFar = Number.MAX_VALUE;

	// for x
	if(direction.x == 0)
	{
		if(origin.x < bboxMin.x || origin.x > bboxMax.x)
		{
			// ray missed the box
			return false;
		}
	}
	else
	{
		let t1 = (bboxMin.x - origin.x)/direction.x;
		let t2 = (bboxMax.x - origin.x)/direction.x;
		if(t1 > t2)
		{
			let temp = t2;
			t2 = t1;
			t1 = temp;
		}
		if(t1 > fNear)
			fNear = t1;
		if(t2 < fFar)
			fFar = t2;
		if(fNear > fFar) 
		{ 
			// missed, return false 
			return false;
		}
		if(fFar < 0) 
		{ 
			// box behind ray, return false 
			return false;
		}
	}

	// for y
	if(direction.y == 0)
	{
		if(origin.y < bboxMin.y || origin.y > bboxMax.y)
		{
			// ray missed the box
			return false;
		}
	}
	else
	{
		let t1 = (bboxMin.y - origin.y)/direction.y;
		let t2 = (bboxMax.y - origin.y)/direction.y;
		if(t1 > t2)
		{
			let temp = t2;
			t2 = t1;
			t1 = temp;
		}
		if(t1 > fNear)
			fNear = t1;
		if(t2 < fFar)
			fFar = t2;
		if(fNear > fFar) 
		{ 
			// missed, return false 
			return false;
		}
		if(fFar < 0) 
		{ 
			// box behind ray, return false 
			return false;
		}
	}

	// for z
	if(direction.z == 0)
	{
		if(origin.z < bboxMin.z || origin.z > bboxMax.z)
		{
			// ray missed the box
			return false;
		}
	}
	else
	{
		let t1 = (bboxMin.z - origin.z)/direction.z;
		let t2 = (bboxMax.z - origin.z)/direction.z;
		if(t1 > t2)
		{
			let temp = t2;
			t2 = t1;
			t1 = temp;
		}
		if(t1 > fNear)
			fNear = t1;
		if(t2 < fFar)
			fFar = t2;
		if(fNear > fFar) 
		{ 
			// missed, return false 
			return false;
		}
		if(fFar < 0) 
		{ 
			// box behind ray, return false 
			return false;
		}
	}

	// if we got here, that means we have an intersection
	return { min: fNear, max: fFar};
}
/*****************************************************************************/
static computeBarycentricCoords(vecPoint, vecVertices)
{
    let faceSide1 = BABYLON.Vector3.Zero();
    let faceSide2 = BABYLON.Vector3.Zero();
    let faceNormal = BABYLON.Vector3.Zero();

    vecVertices[1].subtractToRef(vecVertices[0], faceSide1);
    vecVertices[2].subtractToRef(vecVertices[0], faceSide2);
	BABYLON.Vector3.CrossToRef(faceSide1, faceSide2, faceNormal);
	const fArea = faceNormal.length()/2;

	vecVertices[2].subtractToRef(vecVertices[1], faceSide1);
	let vecFromPoint = vecPoint.subtract(vecVertices[1]);
	let vecCross = BABYLON.Vector3.Cross(faceSide1, vecFromPoint);
	let u = (vecCross.length()/2)/fArea;

	vecVertices[0].subtractToRef(vecVertices[2], faceSide2);
	vecPoint.subtractToRef(vecVertices[2], vecFromPoint);
	BABYLON.Vector3.CrossToRef(faceSide2, vecFromPoint, vecCross);
	let v = (vecCross.length()/2)/fArea;

	return { u: u, v: v, w: 1.0 - u - v };
}
/*****************************************************************************/
static doesBBoxIntersectFace(vecBBoxMin, vecBBoxMax, faceInfoOrVertices)
{
	let faceVerts = faceInfoOrVertices.globalVerts ? faceInfoOrVertices.globalVerts : faceInfoOrVertices;

	// Triangle min-max
	_cache_tricd_tMin.copyFrom(faceVerts[0]);
	_cache_tricd_tMin.minimizeInPlace(faceVerts[1]);
	_cache_tricd_tMin.minimizeInPlace(faceVerts[2]);

	_cache_tricd_tMax.copyFrom(faceVerts[0]);
	_cache_tricd_tMax.maximizeInPlace(faceVerts[1]);
	_cache_tricd_tMax.maximizeInPlace(faceVerts[2]);

	if(_cache_tricd_tMin.x >= vecBBoxMax.x || _cache_tricd_tMax.x <= vecBBoxMin.x
		|| _cache_tricd_tMin.y >= vecBBoxMax.y || _cache_tricd_tMax.y <= vecBBoxMin.y
		|| _cache_tricd_tMin.z >= vecBBoxMax.z || _cache_tricd_tMax.z <= vecBBoxMin.z)
		return false;

	let center = vecBBoxMin.add(vecBBoxMax).scale(0.5);
	let h = vecBBoxMax.subtract(center);

	let v1FromCenter = faceVerts[0].subtract(center);

	faceVerts[1].subtractToRef(faceVerts[0], _cache_tricd_t[0]);
	faceVerts[2].subtractToRef(faceVerts[0], _cache_tricd_t[1]);

	let faceNormal = faceInfoOrVertices.normal;
	if(!faceNormal)
	{
		BABYLON.Vector3.CrossToRef(_cache_tricd_t[1], _cache_tricd_t[0], _cache_tricd_faceNormal);
		_cache_tricd_faceNormal.normalize();
		faceNormal = _cache_tricd_faceNormal;
	}

	_cache_tricd_absNormal.copyFromFloats(Math.abs(faceNormal.x), Math.abs(faceNormal.y), Math.abs(faceNormal.z));
	let s = Dot3(faceNormal, v1FromCenter);
	let r = Math.abs(Dot3(h, _cache_tricd_absNormal));
	if(Math.abs(s) >= r)
		return false;

	let v2FromCenter = faceVerts[1].subtract(center);
	let v3FromCenter = faceVerts[2].subtract(center);

	faceVerts[2].subtractToRef(faceVerts[1], _cache_tricd_t[2]);

	_cache_tricd_absT[0].copyFromFloats(Math.abs(_cache_tricd_t[0].x), Math.abs(_cache_tricd_t[0].y), Math.abs(_cache_tricd_t[0].z));
	_cache_tricd_absT[1].copyFromFloats(Math.abs(_cache_tricd_t[1].x), Math.abs(_cache_tricd_t[1].y), Math.abs(_cache_tricd_t[1].z));
	_cache_tricd_absT[2].copyFromFloats(Math.abs(_cache_tricd_t[2].x), Math.abs(_cache_tricd_t[2].y), Math.abs(_cache_tricd_t[2].z));

	// SAT test all cross-axes.

	// eX <cross> t[0]
	let d1 = _cache_tricd_t[0].y * v1FromCenter.z - _cache_tricd_t[0].z * v1FromCenter.y;
	let d2 = _cache_tricd_t[0].y * v3FromCenter.z - _cache_tricd_t[0].z * v3FromCenter.y;
	let tc = (d1 + d2) * 0.5;
	r = Math.abs(h.y * _cache_tricd_absT[0].z + h.z * _cache_tricd_absT[0].y);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	// eX <cross> t[1]
	d1 = _cache_tricd_t[1].y * v1FromCenter.z - _cache_tricd_t[1].z * v1FromCenter.y;
	d2 = _cache_tricd_t[1].y * v2FromCenter.z - _cache_tricd_t[1].z * v2FromCenter.y;
	tc = (d1 + d2) * 0.5;
	r = Math.abs(h.y * _cache_tricd_absT[1].z + h.z * _cache_tricd_absT[1].y);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	// eX <cross> t[2]
	d1 = _cache_tricd_t[2].y * v1FromCenter.z - _cache_tricd_t[2].z * v1FromCenter.y;
	d2 = _cache_tricd_t[2].y * v2FromCenter.z - _cache_tricd_t[2].z * v2FromCenter.y;
	tc = (d1 + d2) * 0.5;
	r = Math.abs(h.y * _cache_tricd_absT[2].z + h.z * _cache_tricd_absT[2].y);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	// eY <cross> t[0]
	d1 = _cache_tricd_t[0].z * v1FromCenter.x - _cache_tricd_t[0].x * v1FromCenter.z;
	d2 = _cache_tricd_t[0].z * v3FromCenter.x - _cache_tricd_t[0].x * v3FromCenter.z;
	tc = (d1 + d2) * 0.5;
	r = Math.abs(h.x * _cache_tricd_absT[0].z + h.z * _cache_tricd_absT[0].x);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	// eY <cross> t[1]
	d1 = _cache_tricd_t[1].z * v1FromCenter.x - _cache_tricd_t[1].x * v1FromCenter.z;
	d2 = _cache_tricd_t[1].z * v2FromCenter.x - _cache_tricd_t[1].x * v2FromCenter.z;
	tc = (d1 + d2) * 0.5;
	r = Math.abs(h.x * _cache_tricd_absT[1].z + h.z * _cache_tricd_absT[1].x);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	// eY <cross> t[2]
	d1 = _cache_tricd_t[2].z * v1FromCenter.x - _cache_tricd_t[2].x * v1FromCenter.z;
	d2 = _cache_tricd_t[2].z * v2FromCenter.x - _cache_tricd_t[2].x * v2FromCenter.z;
	tc = (d1 + d2) * 0.5;
	r = Math.abs(h.x * _cache_tricd_absT[2].z + h.z * _cache_tricd_absT[2].x);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	// eZ <cross> t[0]
	d1 = _cache_tricd_t[0].x * v1FromCenter.y - _cache_tricd_t[0].y * v1FromCenter.x;
	d2 = _cache_tricd_t[0].x * v3FromCenter.y - _cache_tricd_t[0].y * v3FromCenter.x;
	tc = (d1 + d2) * 0.5;
	r = Math.abs(h.y * _cache_tricd_absT[0].x + h.x * _cache_tricd_absT[0].y);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	// eZ <cross> t[1]
	d1 = _cache_tricd_t[1].x * v1FromCenter.y - _cache_tricd_t[1].y * v1FromCenter.x;
	d2 = _cache_tricd_t[1].x * v2FromCenter.y - _cache_tricd_t[1].y * v2FromCenter.x;
	tc = (d1 + d2) * 0.5;
	r = Math.abs(h.y * _cache_tricd_absT[1].x + h.x * _cache_tricd_absT[1].y);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	// eZ <cross> t[2]
	d1 = _cache_tricd_t[2].x * v1FromCenter.y - _cache_tricd_t[2].y * v1FromCenter.x;
	d2 = _cache_tricd_t[2].x * v2FromCenter.y - _cache_tricd_t[2].y * v2FromCenter.x;
	tc = (d1 + d2) * 0.5;
	r = Math.abs(h.y * _cache_tricd_absT[2].x + h.x * _cache_tricd_absT[2].y);
	if(r + Math.abs(tc - d1) < Math.abs(tc))
		return false;

	return true;
/*
    // This was used in distribute over the surface node, but seems incorrect - keeping it here
    // for possible future debugging of that node.
	let vecTempDir = BABYLON.Vector3.Zero();

	let iVertex;
	const numVerts = faceVerts.length;
	for(iVertex = 0; iVertex < numVerts; iVertex++)
	{
		faceVerts[(iVertex + 1) % numVerts].subtractToRef(faceVerts[iVertex], vecTempDir);
		const fLen = vecTempDir.length();
		vecTempDir.normalize();

		let pRay = new BABYLON.Ray(faceVerts[iVertex], vecTempDir, fLen);
		if(pRay.intersectsBoxMinMax(vecBBoxMin, vecBBoxMax, 1e-4))
			break;
	}

	// If we intersect a side, we clearly intersect a face
	if(iVertex < numVerts)
		return true;

	// Otherwise, we're either completely inside or completely outside:
	// Note that we provide a pretty meaty epsilon because we want to err on the side of intersecting things
	return Utils3D.isPointInTriangle(vecBBoxMax.add(vecBBoxMin).scale(0.5), faceVerts, false, 0, 0.1);
*/
}
/*****************************************************************************/
static computeNextDirectionAlongSurface(currDir, currNormal, newNormal, upAxis)
{
	let fAngle = -Utils3D.getAngleBetweenVectorsOnPlane(currNormal, newNormal, upAxis);
	let rotateMatrix = BABYLON.Matrix.RotationAxis(upAxis, fAngle/180.0*Math.PI);
	return BABYLON.Vector3.TransformNormal(currDir, rotateMatrix);
}
/*****************************************************************************/
static setWorldPivot(pSrcMesh, vecWorldPivot)
{
	if(!pSrcMesh)
		return;

	let pTransforms = Utils3D.getMeshWorldTransforms(pSrcMesh);
	pSrcMesh.position = pTransforms.translation;
	pSrcMesh.rotation = pTransforms.quaternion.toEulerAngles();
	pSrcMesh.scaling = pTransforms.scale;
	pSrcMesh.setPivotMatrix(BABYLON.Matrix.Identity());

	let vecOldPosition = pSrcMesh.position.clone();
	pSrcMesh.setPivotPoint(vecWorldPivot, BABYLON.Space.WORLD);
	pSrcMesh.position = vecOldPosition.add(pSrcMesh.position.subtract(pSrcMesh.getAbsolutePosition()));
}
/*****************************************************************************/
static disposeOfMaterialsAndTextures(pMesh, forceDisposeTextures = false)
{
	if(!pMesh)
		return;

	let pMaterial = pMesh.material;
	if(!pMaterial)
		return;

	if(pMaterial.getBindedMeshes().length > 1)
		return;

	if(pMesh instanceof BABYLON.InstancedMesh)
		return;

	pMesh.material = null;
	if(pMaterial.subMaterials)
	{
		for(let pSubmaterial of pMaterial.subMaterials)
		{
			if(pSubmaterial.getBindedMeshes().length > 1)
				pSubmaterial.dispose(false, forceDisposeTextures);
		}
	}

	pMaterial.dispose(false, forceDisposeTextures);
}
/*****************************************************************************/
static clearAssetAttributes(pSrcMesh, parentNodeId)
{
	// We need to clone the mesh here because if we don't, subsequent operations are going
	// to be modifying the mesh and vertex data stored in the asset cacher directly.
	let pNewMesh = Utils3D.cloneMesh(pSrcMesh, pSrcMesh.name);
	pNewMesh.parentNodeId = parentNodeId;
	pNewMesh.bakeCurrentTransformIntoVertices();
	// We need to reset materials, otherwise, we're going to be assigning textures to the
	// material stored in the asset cacher...
	pNewMesh.material = null;

	return pNewMesh;
}
/*****************************************************************************/
static cloneCustomAttributes(pTargetMesh, pSrcMesh)
{
	if(pSrcMesh.modsyMeshType)
		pTargetMesh.modsyMeshType = pSrcMesh.modsyMeshType;
	if(pSrcMesh.modsyMeshSubtype)
		pTargetMesh.modsyMeshSubtype = pSrcMesh.modsyMeshSubtype;
	if(pSrcMesh.modsyAttachedObjectId)
		pTargetMesh.modsyAttachedObjectId = pSrcMesh.modsyAttachedObjectId;
	if(pSrcMesh.extraModelInfo)
		pTargetMesh.extraModelInfo = CoreUtils.clone(pSrcMesh.extraModelInfo);

	if(pSrcMesh.sourceRoomId)
		pTargetMesh.sourceRoomId = pSrcMesh.sourceRoomId;
	if(pSrcMesh.parentModelId)
		pTargetMesh.parentModelId = pSrcMesh.parentModelId;

	if(pSrcMesh.origRotationQuaternion)
		pTargetMesh.origRotationQuaternion = pSrcMesh.origRotationQuaternion.clone();
	if(pSrcMesh.parentGraphId) {
		pTargetMesh.parentGraphId = pSrcMesh.parentGraphId;
		let parentGraph = NodeEngine.getInstance().getGraph(pSrcMesh.parentGraphId);
		if(parentGraph)
			parentGraph.adoptMesh(pTargetMesh);
		else
			console.error(`cloneCustomAttributes: Graph cannot be found for a mesh with a valid graph id!`);
	}
	if(pSrcMesh.parentNodeId)
		pTargetMesh.parentNodeId = pSrcMesh.parentNodeId;
	if(pSrcMesh.parentMaterialNodeId)
		pTargetMesh.parentMaterialNodeId = pSrcMesh.parentMaterialNodeId;
	if(pSrcMesh.parentMaterialTweaksNodeId)
		pTargetMesh.parentMaterialTweaksNodeId = pSrcMesh.parentMaterialTweaksNodeId;

	if(pSrcMesh.localVoxelGrid)
		pTargetMesh.localVoxelGrid = pSrcMesh.localVoxelGrid;
}

static snapToPrimaryAxes(point, origin, maxSnappingDistance = 0.2, axes = ['x', 'y']) {
	let modifiedPoint = point.clone();
	let closestAxis = null;
	let closestDistance = Number.MAX_VALUE;
	for (let axis of axes) {
		let distance = Math.abs(origin[axis] - point[axis]);
		if (distance < maxSnappingDistance && distance < closestDistance) {
			closestDistance = distance;
			closestAxis = axis;
		}
	}
	if (closestAxis) {
		modifiedPoint[closestAxis] = origin[closestAxis];
	}
	return modifiedPoint;
}

/*****************************************************************************/
// groundPoint, origin : BABYLON:Vector3
// angleIncrement : number[]
// maxSnappingDistance : number (degrees)
static snapToAngle(groundPoint, origin, angleIncrement = 45, bPreserveLineLength = true, maxSnappingAngle = 5)
{
    if(!origin)
        return groundPoint;

    let internalGroundPoint = groundPoint;
    let internalOrigin = origin;
    const is2d = groundPoint.z == undefined;
    if(is2d) {
        internalGroundPoint = new BABYLON.Vector3(groundPoint.x, 0 ,groundPoint.y);
        internalOrigin = new BABYLON.Vector3(origin.x, 0, origin.y);
    }

    if(angleIncrement != _cache_snapping_increment) {
        let snapAngleVectors = [];
        const subdivisions = Math.floor(360/angleIncrement);

        for (let i = 0; i < subdivisions; i++) {
            let angleInRad = i * angleIncrement * Math.PI / 180;
            const x = Math.cos(angleInRad);
            const z = Math.sin(angleInRad);
            const snapVec = new BABYLON.Vector3(x, 0, z);
            snapAngleVectors.push(snapVec);
        }
        _cache_snapping_directions = snapAngleVectors;
        _cache_snapping_increment = angleIncrement;
    }

    const distToOrigin = BABYLON.Vector3.Distance(internalOrigin, internalGroundPoint);
    let closestAngle = Number.MAX_VALUE;
    let closestPoint = internalGroundPoint;
    const initialDirection = internalGroundPoint.subtract(internalOrigin);
    const rotationNormal = new BABYLON.Vector3(0, 1, 0);
    for (let i = 0; i < _cache_snapping_directions.length; i++) {
        const angleToPoint = Math.abs(Utils3D.getAngleBetweenVectorsOnPlane(initialDirection, _cache_snapping_directions[i], rotationNormal));
        if(angleToPoint <= maxSnappingAngle && angleToPoint < closestAngle) {
			if(bPreserveLineLength)
				closestPoint = internalOrigin.add(_cache_snapping_directions[i].scale(distToOrigin));
			else {
				const distAlongDir = BABYLON.Vector3.Dot(_cache_snapping_directions[i], initialDirection);
				closestPoint = internalOrigin.add(_cache_snapping_directions[i].scale(distAlongDir));
			}
            closestAngle = angleToPoint;
        }
    }

    return is2d ? new BABYLON.Vector2(closestPoint.x, closestPoint.z) : closestPoint;
}
/*****************************************************************************/
static cloneMesh(pSrcMesh, strCopyName = null, bCloneVertices = true, bCreateInstance = false)
{
	let pTrueSourceMesh = pSrcMesh;
	const bIsAlreadyAnInstance = pSrcMesh instanceof BABYLON.InstancedMesh;
	if(bIsAlreadyAnInstance)
		pTrueSourceMesh = pSrcMesh.sourceMesh;

	if(!strCopyName)
		strCopyName = pSrcMesh.name + '-copy';

	let pMeshCopy;
	if(bCreateInstance)
		pMeshCopy = pTrueSourceMesh.createInstance(strCopyName);
	else
	{
		// We "turn off" refreshBoundingInfo because BJS stupidly runs it on every single
		// vertex when making an exact copy of the source mesh, which turns out to be really
		// slow for large meshes. Intead, we simply copy the bounding info from the source...
		let oldRefreshBoundingInfo = BABYLON.Mesh.prototype.refreshBoundingInfo;
		BABYLON.Mesh.prototype.refreshBoundingInfo = () => {};
		pMeshCopy = pTrueSourceMesh.clone(strCopyName, null, true);
		let sourceBBox = pTrueSourceMesh.getBoundingInfo().boundingBox;
		pMeshCopy.buildBoundingInfo(sourceBBox.minimum, sourceBBox.maximum, pTrueSourceMesh.getWorldMatrix());
		BABYLON.Mesh.prototype.refreshBoundingInfo = oldRefreshBoundingInfo;
	}		

	if(bCreateInstance || bIsAlreadyAnInstance)
	{
		pMeshCopy.position = pSrcMesh.position.clone();
		pMeshCopy.rotation = pSrcMesh.rotation.clone();
		if(pSrcMesh.rotationQuaternion)
			pMeshCopy.rotationQuaternion = pSrcMesh.rotationQuaternion.clone();
		pMeshCopy.scaling = pSrcMesh.scaling.clone();
	}

	if(bCloneVertices && pMeshCopy.makeGeometryUnique && !bCreateInstance)
		pMeshCopy.makeGeometryUnique();
	pMeshCopy.setPivotMatrix(pSrcMesh.getPivotMatrix().clone());

	Utils3D.cloneCustomAttributes(pMeshCopy, pSrcMesh);

	// Apparently, the enabled flag isn't copied over...
	const isSourceEnabled = pSrcMesh.isEnabled(false);
	pMeshCopy.setEnabled(isSourceEnabled);

	return pMeshCopy;
}
/*****************************************************************************/
static isPointInsideMesh(mesh, point) 
{
	if(!mesh)
		return false;

	let boundInfo = mesh.getBoundingInfo();
	let max = boundInfo.boundingBox.maximumWorld;
	let min = boundInfo.boundingBox.minimumWorld;
	let diameter = 2 * boundInfo.boundingSphere.radiusWorld;
	if(point.x < min.x || point.x > max.x) {
		return false;
	}
	if(point.y < min.y || point.y > max.y) {
		return false;
	}
	if(point.z < min.z || point.z > max.z) {
		return false;
	}

	let pointFound = false;
	let hitCount = 0;
	let ray = new BABYLON.Ray(BABYLON.Vector3.Zero(), BABYLON.Axis.X, diameter);
	let pickInfo;
    let refPoint = point.clone();
	
	hitCount = 0;
	ray.origin = refPoint;
    ray.distance = diameter;		
	pickInfo = ray.intersectsMesh(mesh);
	while (pickInfo.hit) {	
		hitCount++;
		pickInfo.pickedPoint.addToRef(ray.direction.scale(0.00000001), refPoint);
		ray.origin  = refPoint;
		pickInfo = ray.intersectsMesh(mesh);
	}	
	if((hitCount % 2) === 1) {
		pointFound = true;
	}
	
	return pointFound;
}
/*****************************************************************************/
static isPointInBBox(vecPoint, vecMin, vecMax)
{
	return vecPoint.x >= vecMin.x && vecPoint.x <= vecMax.x &&
		vecPoint.y >= vecMin.y && vecPoint.y <= vecMax.y &&
		vecPoint.z >= vecMin.z && vecPoint.z <= vecMax.z;
}
/*****************************************************************************/
static areMaterialsEqual(material1, material2)
{
	if(!material1 || !material2)
		return material1 === material2;

	// Material types must be the same
	if((material1.diffuseColor && !material2.diffuseColor)
		|| (!material1.diffuseColor && material2.diffuseColor))
		return false;

	// These interfere with proper testing
	const skippedProps = {
		'_indexInSceneMaterialArray': true,
		'uniqueId': true,
	};

	for(let propertyName of Object.keys(material1))
	{
		if(skippedProps[propertyName])
			continue;

		if(typeof material1[propertyName] === 'number' || typeof material2[propertyName] === 'number')
		{
			if(material1[propertyName] === undefined || material2[propertyName] === undefined)
			{
				// CoreUtils.log(`Material different prop: ${propertyName} values = ${material1[propertyName]} ${material2[propertyName]}`)
				return false;
			}				

			if(Math.abs(material1[propertyName] - material2[propertyName]) > 1e-5)
			{
				// CoreUtils.log(`Material different prop: ${propertyName} values = ${material1[propertyName]} ${material2[propertyName]}`)
				return false;
			}
		}
		else if(material1[propertyName] instanceof BABYLON.Color3 || material2[propertyName] instanceof BABYLON.Color3)
		{
			if(!material1[propertyName] || !material2[propertyName])
			{
				// CoreUtils.log(`Material different prop: ${propertyName} values = ${material1[propertyName]} ${material2[propertyName]}`)
				return false;
			}

			let diff = material1[propertyName].subtract(material2[propertyName]);
			if(Math.abs(diff.r) > 1e-5 || Math.abs(diff.g) > 1e-5 || Math.abs(diff.b) > 1e-5) {
				// CoreUtils.log(`Material different prop: ${propertyName} values = ${material1[propertyName]} ${material2[propertyName]}`)
				return false;
			}				
		}
		else if(material1[propertyName] instanceof BABYLON.Texture || material2[propertyName] instanceof BABYLON.Texture)
		{
			if(material1[propertyName]?.uniqueId != material2[propertyName]?.uniqueId)
			{
				// CoreUtils.log(`Material different prop: ${propertyName} values = ${material1[propertyName]} ${material2[propertyName]}`)
				return false;
			}
		}
	}

	return true;
}
/*****************************************************************************/
static cloneMaterialWithoutTextures(pSrcMaterial)
{
	// Note that the elaborate pointer setting is basically done to avoid cloning 
	// the textures and associated bitmaps many times over
	let matCopy;
	if(pSrcMaterial.diffuseColor)
	{
		// Standard material
		let oldMatTexture = pSrcMaterial.diffuseTexture;
		pSrcMaterial.diffuseTexture = null;
		matCopy = pSrcMaterial.clone();
		pSrcMaterial.diffuseTexture = oldMatTexture;
		matCopy.diffuseTexture = oldMatTexture;
		matCopy.diffuseColor = pSrcMaterial.diffuseColor.clone();
		if(matCopy.diffuseTexture)
			matCopy.emissiveColor = matCopy.diffuseColor.clone().scale(0.85);
		else
			matCopy.emissiveColor = new BABYLON.Color3(0.30, 0.30, 0.30);
	}
	else
	{
		// PBR material		
		let oldSourceTextures = {};
		for(let propertyName of Object.keys(pSrcMaterial))
		{
			if(pSrcMaterial[propertyName] instanceof BABYLON.Texture)
			{
				oldSourceTextures[propertyName] = pSrcMaterial[propertyName];
				pSrcMaterial[propertyName] = null;
			}					
		}

		matCopy = pSrcMaterial.clone();

		for(let textureName of Object.keys(oldSourceTextures))
		{
			matCopy[textureName] = oldSourceTextures[textureName];
			pSrcMaterial[textureName] = oldSourceTextures[textureName];
		}
	}

	return matCopy;
}
/*****************************************************************************/
// Note that this assigns textures by reference, but does copy the color
static assignTexturesAndColorsOnto(pTargetMaterial, pSrcMaterial)
{
	Utils3D.unfreezeMaterials(pTargetMaterial);

	if(pSrcMaterial.diffuseColor)
	{
		// Non-PBR material
		if(!pSrcMaterial.diffuseTexture)
		{
			pTargetMaterial.diffuseColor = pSrcMaterial.diffuseColor;
			pTargetMaterial.diffuseTexture = null;
			pTargetMaterial.emissiveColor = pTargetMaterial.diffuseColor.clone().scale(0.85);
		}
		else
		{
			pTargetMaterial.diffuseColor = pSrcMaterial.diffuseColor;
			pTargetMaterial.diffuseTexture = pSrcMaterial.diffuseTexture;
			pTargetMaterial.emissiveColor = new BABYLON.Color3(0.30, 0.30, 0.30);
		}
		pTargetMaterial.specularColor = pSrcMaterial.specularColor.clone();
	}
	else
	{
		// PBR Material
		for(let propertyName of Object.keys(pSrcMaterial))
		{
			if(pSrcMaterial[propertyName] instanceof BABYLON.Texture)
				pTargetMaterial[propertyName] = pSrcMaterial[propertyName];
			else if(pSrcMaterial[propertyName] instanceof BABYLON.Color3 || pSrcMaterial[propertyName] instanceof BABYLON.Color4)
				pTargetMaterial[propertyName] = pSrcMaterial[propertyName].clone();
		}
	}

	Utils3D.freezeMaterials(pTargetMaterial);
}
/*****************************************************************************/
static unparentAndPreserveTransform(pMesh)
{
	if(!pMesh.parent)
		return;

	const pTransforms = Utils3D.getMeshWorldTransforms(pMesh);
	pMesh.parent = null;
	pMesh.scaling = pTransforms.scale;
	pMesh.scaling.scaleInPlace(GlobalDefs.fGlobalInputScale);
	pMesh.rotationQuaternion = null;
	pMesh.origRotationQuaternion = pTransforms.quaternion;
	pMesh.position = pTransforms.translation;
	let vecAngles = pTransforms.quaternion.toEulerAngles();
	pMesh.rotation = vecAngles;
}
/*****************************************************************************/
static createDefaultSubmeshes(pMesh, allSubmeshRanges)
{
	//let verticesCount = pMesh.getTotalVertices();

	// We now need to create submeshes for all the faces in between:
	let totalIndices = pMesh.getTotalIndices();
	allSubmeshRanges.push({ start: -1, end: 0 });
	allSubmeshRanges.push({ start: totalIndices, end: totalIndices + 3 });

	allSubmeshRanges.sort((a, b) => { return a.start - b.start; });

	let meshIndices = pMesh.getIndices();		
	for(let rangeIdx = 1; rangeIdx < allSubmeshRanges.length; rangeIdx++)
	{
		const currRange = allSubmeshRanges[rangeIdx];
		const prevRange = allSubmeshRanges[rangeIdx - 1];
		if(prevRange.end === currRange.start)
			continue;

		// Figure out our vertex ranges
		let minVertexIndex = Number.MAX_VALUE;
		let maxVertexIndex = -Number.MAX_VALUE;
		for(let currIndicee = prevRange.end; currIndicee < currRange.start; currIndicee++)
		{
			let currVertexIndex = meshIndices[currIndicee];
			if(currVertexIndex < minVertexIndex)
				minVertexIndex = currVertexIndex;
			if(currVertexIndex > maxVertexIndex)
				maxVertexIndex = currVertexIndex;
		}

		// Otherwise, create a submesh in between:
		let iNumVerts = maxVertexIndex - minVertexIndex + 3;
		new BABYLON.SubMesh(0, minVertexIndex, iNumVerts, prevRange.end, currRange.start - prevRange.end, pMesh);
		//new BABYLON.SubMesh(0, 0, verticesCount, prevRange.end, currRange.start - prevRange.end, pMesh);
	}

	pMesh.subMeshes.forEach((submesh) => { submesh.materialIndex = 0; });
}
/*****************************************************************************/
static areTrianglesIdentical(tri1, tri2, epsilon = 1e-7)
{
	let idx1;
	for(idx1 = 0; idx1 < tri1.length; idx1++)
	{
		if(BABYLON.Vector3.DistanceSquared(tri1[idx1], tri2[idx1]) > epsilon*epsilon)
			break;
	}

	if(idx1 === tri1.length)
		return true;

	let copy = tri2.slice();
	copy.reverse();
	for(idx1 = 0; idx1 < tri1.length; idx1++)
	{
		if(BABYLON.Vector3.DistanceSquared(tri1[idx1], copy[idx1]) > epsilon*epsilon)
			break;
	}

	if(idx1 === tri1.length)
		return true;

	return false;
}
/*****************************************************************************/
static freezeMaterialsImmediate(material)
{
	if(!material)
		return;

	if(material.subMaterials)
		material.subMaterials.forEach(mat => { 
			if(!material.isFrozen)
				mat.freeze();
		});

	if(!material.isFrozen)
		material.freeze();
}
/*****************************************************************************/
static freezeMaterials(material)
{
	if(!material)
		return;

	let scene = material.getScene();
	if(!scene.materialsToFreeze)
	{
		scene.materialsToFreeze = {};
		scene.onAfterRenderObservable.add((pScene) => {
			for(let key in pScene.materialsToFreeze)
			{
				const currMaterial = pScene.materialsToFreeze[key];
				Utils3D.freezeMaterialsImmediate(currMaterial);
			}
			scene.materialsToFreeze = {};
		});
	}

	scene.materialsToFreeze[material.uniqueId] = material;
}
/*****************************************************************************/
static unfreezeMaterials(material)
{
	if(!material)
		return;

	if(material.subMaterials)
		material.subMaterials.forEach(mat => { 
			if(material.isFrozen)
				mat.unfreeze();
		});

	if(material.isFrozen)
		material.unfreeze();

}
/*****************************************************************************/
static directionToQuaternion(direction)
{
	let vecUpAxisOut = BABYLON.Vector3.Zero();
	let vecSideAxisOut = BABYLON.Vector3.Zero();
	Utils3D.getCoordSystemFromNormal(direction, vecUpAxisOut, vecSideAxisOut);

	return BABYLON.Quaternion.FromLookDirectionLH(direction, vecUpAxisOut);
}
/*****************************************************************************/
static directionToRotationMatrix(vecForwardDirection, matrixOut)
{
	let vecUpAxisOut = BABYLON.Vector3.Zero();
	let vecSideAxisOut = BABYLON.Vector3.Zero();
	Utils3D.getCoordSystemFromNormal(vecForwardDirection, vecUpAxisOut, vecSideAxisOut);

	BABYLON.Matrix.FromValuesToRef(vecSideAxisOut.x, vecSideAxisOut.y, vecSideAxisOut.z, 0.0, 
							vecUpAxisOut.x, vecUpAxisOut.y, vecUpAxisOut.z, 0.0, 
							vecForwardDirection.x, vecForwardDirection.y, vecForwardDirection.z, 0.0, 
							0, 0, 0, 1.0, matrixOut);
}
/*****************************************************************************/
static getCoordSystemFromNormal(vecNormal, vecUpAxisOut, vecSideAxisOut)
{
	if(vecNormal.y > 0.999)
	{
		vecSideAxisOut.set(1.0, 0, 0);
		vecUpAxisOut.set(0, 0, 1);
	}
	else if(vecNormal.y < -0.999)
	{
		vecSideAxisOut.set(1.0, 0, 0);
		vecUpAxisOut.set(0, 0, -1);
	}
	else
	{
		// For side axis, rotate 90 degrees around Y
		Utils2D.rotatePointToRef({x: vecNormal.x, y: vecNormal.z }, 90, vecSideAxisOut);
		vecSideAxisOut.z = vecSideAxisOut.y;
		vecSideAxisOut.y = 0;
		vecSideAxisOut.normalize();

		BABYLON.Vector3.CrossToRef(vecSideAxisOut, vecNormal, vecUpAxisOut);
		vecUpAxisOut.normalize();
	}
}
/*****************************************************************************/
static getAxesFromDirectionMatrix(directionMatrix) {
	let upAxis = BABYLON.Vector3.Zero();
	let sideAxis = BABYLON.Vector3.Zero();
	let forwardAxis = BABYLON.Vector3.Zero();
	directionMatrix.getRowToRef(0, sideAxis);
	directionMatrix.getRowToRef(1, upAxis);
	directionMatrix.getRowToRef(2, forwardAxis);

	return {
		xAxis: sideAxis,
		yAxis: upAxis,
		zAxis: forwardAxis
	};
}
/*****************************************************************************/
static getPlanePlaneIntersection(plane1, plane2)
{
	const n1 = plane1.normal;
    const n2 = plane2.normal;

    // Find the direction of the line of intersection (cross product of normals)
    const direction = Vector3.Cross(n1, n2);

    // Check if the planes are parallel
    if (direction.lengthSquared() < 1e-6) {
        return null; // Planes are parallel
    }

    direction.normalize();

    // Solving for a point on the line of intersection
    // We'll use Cramer's rule to find a point that satisfies both plane equations
    const A1 = n1.x, B1 = n1.y, C1 = n1.z, D1 = plane1.d;
    const A2 = n2.x, B2 = n2.y, C2 = n2.z, D2 = plane2.d;

    // The line of intersection is found by setting one coordinate to zero and solving
    let point;
    if (Math.abs(direction.z) > Math.abs(direction.x) && Math.abs(direction.z) > Math.abs(direction.y)) {
        // Solve for X and Y when Z = 0
        const determinant = A1 * B2 - A2 * B1;
        if (Math.abs(determinant) < 1e-6) return null; // Planes are effectively coplanar

        const x = (B1 * D2 - B2 * D1) / determinant;
        const y = (A2 * D1 - A1 * D2) / determinant;
        point = new Vector3(x, y, 0);
    } else if (Math.abs(direction.y) > Math.abs(direction.x)) {
        // Solve for X and Z when Y = 0
        const determinant = A1 * C2 - A2 * C1;
        if (Math.abs(determinant) < 1e-6) return null;

        const x = (C1 * D2 - C2 * D1) / determinant;
        const z = (A2 * D1 - A1 * D2) / determinant;
        point = new Vector3(x, 0, z);
    } else {
        // Solve for Y and Z when X = 0
        const determinant = B1 * C2 - B2 * C1;
        if (Math.abs(determinant) < 1e-6) return null;

        const y = (C1 * D2 - C2 * D1) / determinant;
        const z = (B2 * D1 - B1 * D2) / determinant;
        point = new Vector3(0, y, z);
    }

    return new BABYLON.Ray(point, direction);
}
/*****************************************************************************/ m
static getRayRayIntersection(ray1, ray2)
{
    const p1 = ray1.origin;
    const p2 = ray2.origin;
    const d1 = ray1.direction.normalize();
    const d2 = ray2.direction.normalize();

    const d1xd2 = Vector3.Cross(d1, d2);
    const denominator = d1xd2.lengthSquared();

    // If the cross product is near zero, the rays are parallel or collinear
	const currResult = { point: null, t1: Number.MAX_VALUE, t2: Number.MAX_VALUE };
    if (denominator < 1e-6) {
        return currResult;
    }

    // Vector between origins
    const p2p1 = p2.subtract(p1);

    // Compute the projection factors for the closest points on each ray
    const t1 = Vector3.Dot(Vector3.Cross(p2p1, d2), d1xd2) / denominator;
    const t2 = Vector3.Dot(Vector3.Cross(p2p1, d1), d1xd2) / denominator;

    // Closest points on each ray
    const closestPoint1 = p1.add(d1.scale(t1));
    const closestPoint2 = p2.add(d2.scale(t2));

    // Check if the points are close enough to be considered an intersection
    if (Vector3.Distance(closestPoint1, closestPoint2) < 1e-6) {
		currResult.point = closestPoint1;
		currResult.t1 = t1;
		currResult.t2 = t2;
    }

    // Otherwise, return the midpoint between the closest points on the rays
    return currResult;
}
/*****************************************************************************/
static areSegmentsIdentical(vecStart1, vecEnd1, vecStart2, vecEnd2, distanceThreshold = 0.001)
{
    let tempVec = BABYLON.Vector3.Zero();

    vecStart1.subtractToRef(vecStart2, tempVec);
    let fDist1a = tempVec.lengthSquared();
    vecEnd1.subtractToRef(vecEnd2, tempVec);
    let fDist2a = tempVec.lengthSquared();

    vecStart1.subtractToRef(vecEnd2, tempVec);
    let fDist1b = tempVec.lengthSquared();
    vecEnd1.subtractToRef(vecStart2, tempVec);
    let fDist2b = tempVec.lengthSquared();

	const thresholdSquared = distanceThreshold*distanceThreshold;
    return (fDist1a < thresholdSquared && fDist2a < thresholdSquared) || (fDist1b < thresholdSquared && fDist2b < thresholdSquared);
}
/*****************************************************************************/
}
